import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Loader2, Plus, Search, ShieldCheck } from "lucide-react";
import { api, apiError, inr, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Layout";
import { StatementDialog } from "@/components/StatementDialog";
import {
  ArchivedCustomers, CustomerRowActions, PendingCustomerEdits, useCustomerRights,
} from "@/components/CustomerAdmin";
import { CustomerChangeHistory, MergeCustomersDialog } from "@/components/CustomerMerge";
import { DocumentJourney } from "@/components/DocumentJourney";
import { SecureExportDialog } from "@/components/SecureExport";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const INVOICE_COLS = [
  ["number", "Invoice", (r) => <Link className="text-primary hover:underline" to={`/invoices/${r.id}`}>{r.number}</Link>],
  ["kind", "Type", (r) => <Badge variant="outline">{String(r.kind || "").replace(/_/g, " ")}</Badge>],
  ["grand_total", "Total", (r) => inr(r.grand_total)],
  ["outstanding", "Outstanding", (r) => inr(r.outstanding)],
  ["status", "Status", (r) => <Badge variant="secondary">{r.status}</Badge>],
  ["date", "Date", (r) => shortDate(r.date)],
];
const PAYMENT_COLS = [
  ["number", "Receipt"], ["amount", "Amount", (r) => inr(r.amount)], ["method", "Method"],
  ["date", "Date", (r) => shortDate(r.date)],
];
const CHALLAN_COLS = [
  ["number", "Challan"], ["invoice_number", "Invoice"], ["status", "Status", (r) => <Badge variant="secondary">{r.status}</Badge>],
  ["dispatch_date", "Dispatch", (r) => shortDate(r.dispatch_date)],
];

const QUOTATION_COLS = [
  ["number", "Number", (r) => <Link className="text-primary hover:underline" to={`/quotations/${r.id}`}>{r.number}</Link>],
  ["project_name", "Project"],
  ["status", "Status", (r) => <Badge variant="secondary">{r.status}</Badge>],
  ["grand_total", "Value", (r) => inr(r.grand_total)],
  ["date", "Date", (r) => shortDate(r.date)],
];
const LEAD_COLS = [["title", "Lead"], ["stage", "Stage"], ["estimated_value", "Value", (r) => inr(r.estimated_value)]];
const ENQUIRY_COLS = [["code", "Code"], ["product_name", "Product"], ["quantity", "Qty"], ["status", "Status"]];
const ORDER_COLS = [["number", "Order"], ["project_name", "Project"], ["grand_total", "Value", (r) => inr(r.grand_total)], ["status", "Status"]];
const FOLLOWUP_COLS = [["due_date", "Due", (r) => shortDate(r.due_date)], ["kind", "Type"], ["status", "Status"], ["notes", "Notes"]];


const SEARCH_DEBOUNCE_MS = 250;

const EMPTY = {
  company: "", contact_person: "", mobile: "", whatsapp: "", email: "", billing_address: "",
  shipping_address: "", city: "", state: "", state_code: "", place_of_supply: "",
  pincode: "", gstin: "", pan: "",
  customer_type: "Company", industry: "", lead_source: "", credit_limit: 0,
  payment_terms: "", notes: "",
};

export function Customers() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [meta, setMeta] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const rights = useCustomerRights();
  const [tab, setTab] = useState("active");

  useEffect(() => {
    api.get("/gst/meta").then(({ data }) => setMeta(data)).catch(() => setMeta(null));
  }, []);

  const pickState = (state) => {
    const row = (meta?.states || []).find((s) => s.state === state);
    setForm((f) => ({ ...f, state, state_code: row?.code || "",
                      place_of_supply: row ? `${row.code}-${row.state}` : state }));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = search ? `&search=${encodeURIComponent(search)}` : "";
      const { data } = await api.get(`/customers?limit=100${query}`);
      setItems(data.items);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [load]);

  const save = async () => {
    try {
      if (!form.company.trim()) return toast.error("Company name is required");
      await api.post("/customers", { ...form, credit_limit: Number(form.credit_limit || 0) });
      toast.success("Customer created");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div>
      <PageHeader
        testId="customers-header"
        title="Customers"
        subtitle="One customer profile carrying the entire business history."
        actions={
          <>
            <Button variant="outline" className="gap-1.5" data-testid="customers-export-button"
              onClick={() => setExportOpen(true)}>
              <ShieldCheck className="h-4 w-4" /> Export (Owner OTP)
            </Button>
            {can("customers", "create") && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-customer-button" className="gap-1.5">
                  <Plus className="h-4 w-4" /> New Customer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>New customer</DialogTitle>
                </DialogHeader>
                <div className="grid gap-3 sm:grid-cols-2">
                  {[
                    ["company", "Company / Name", "customer-company-input"],
                    ["contact_person", "Contact person", "customer-contact-input"],
                    ["mobile", "Mobile", "customer-mobile-input"],
                    ["whatsapp", "WhatsApp", "customer-whatsapp-input"],
                    ["email", "Email", "customer-email-input"],
                    ["gstin", "GSTIN", "customer-gstin-input"],
                    ["pan", "PAN", null],
                    ["city", "City", "customer-city-input"],
                    ["pincode", "Pincode", null],
                    ["industry", "Industry", null],
                    ["lead_source", "Lead source", null],
                    ["payment_terms", "Payment terms", null],
                    ["credit_limit", "Credit limit (₹)", null],
                  ].map(([key, label, tid]) => (
                    <Field key={key} label={label}>
                      <Input
                        data-testid={tid || undefined}
                        value={form[key]}
                        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                      />
                    </Field>
                  ))}
                  <Field label="State (for GST)">
                    <Select value={form.state} onValueChange={pickState}>
                      <SelectTrigger data-testid="customer-state-input">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        {(meta?.states || []).map((s) => (
                          <SelectItem key={s.code} value={s.state}>
                            {s.code} — {s.state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Place of supply">
                    <Input data-testid="customer-place-of-supply-input" readOnly
                      value={form.place_of_supply || ""} />
                  </Field>
                  {form.state && meta?.company_state && (
                    <div className="sm:col-span-2" data-testid="customer-gst-hint">
                      <Badge variant={form.state === meta.company_state ? "secondary" : "outline"}>
                        {form.state === meta.company_state
                          ? `Within ${meta.company_state} — CGST + SGST will apply`
                          : `Other state — IGST will apply (we are in ${meta.company_state})`}
                      </Badge>
                    </div>
                  )}
                  <Field label="Billing address" className="sm:col-span-2">
                    <Textarea
                      data-testid="customer-billing-input"
                      value={form.billing_address}
                      onChange={(e) => setForm({ ...form, billing_address: e.target.value })}
                    />
                  </Field>
                  <Field label="Shipping / site address" className="sm:col-span-2">
                    <Textarea
                      value={form.shipping_address}
                      onChange={(e) => setForm({ ...form, shipping_address: e.target.value })}
                    />
                  </Field>
                </div>
                <DialogFooter>
                  <Button data-testid="customer-save-button" onClick={save}>
                    Save customer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
          </>
        }
      />
      <SecureExportDialog entity="customers" open={exportOpen} onOpenChange={setExportOpen} />
      <Tabs value={tab} onValueChange={setTab}>
        <div className="border-b border-border px-4 pt-3 sm:px-6">
          <TabsList data-testid="customers-tabs">
            <TabsTrigger value="active" data-testid="customers-tab-active">Active</TabsTrigger>
            <TabsTrigger value="pending" data-testid="customers-tab-pending">Pending Edits</TabsTrigger>
            {rights.can_delete_customers && (
              <TabsTrigger value="history" data-testid="customers-tab-history">Change History</TabsTrigger>
            )}
            {rights.can_delete_customers && (
              <TabsTrigger value="archived" data-testid="customers-tab-archived">Archived</TabsTrigger>
            )}
          </TabsList>
        </div>

        <TabsContent value="active">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3 sm:px-6">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="customers-search-input"
            className="pl-9"
            placeholder="Search company, mobile, GSTIN, city"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {rights.can_delete_customers && (
          <MergeCustomersDialog customers={items} onDone={load} />
        )}
      </div>
      <div className="p-4 sm:p-6">
        {loading ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : items.length === 0 ? (
          <Empty title="No customers yet" hint="Create your first customer to start quoting." />
        ) : (
          <div className="surface overflow-x-auto" data-testid="customers-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="hidden md:table-cell">City / State</TableHead>
                  <TableHead className="hidden lg:table-cell">GSTIN</TableHead>
                  <TableHead className="text-right">Credit limit</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((c) => (
                  <TableRow key={c.id} data-testid={`customer-row-${c.id}`}>
                    <TableCell>
                      <Link
                        to={`/customers/${c.id}`}
                        className="font-medium text-primary hover:underline"
                        data-testid={`customer-link-${c.id}`}
                      >
                        {c.company}
                      </Link>
                      <div className="text-xs text-muted-foreground">{c.code}</div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {c.contact_person}
                      <div className="text-xs text-muted-foreground">{c.mobile}</div>
                    </TableCell>
                    <TableCell className="hidden text-sm md:table-cell">
                      {c.city} {c.state}
                    </TableCell>
                    <TableCell className="hidden font-mono-num text-xs lg:table-cell">
                      {c.gstin || "-"}
                    </TableCell>
                    <TableCell className="text-right font-mono-num text-sm">
                      {inr(c.credit_limit)}
                    </TableCell>
                    <TableCell className="text-right">
                      <CustomerRowActions customer={c} rights={rights} onChanged={load} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
        </TabsContent>

        <TabsContent value="pending">
          <div className="p-4 sm:p-6">
            <PendingCustomerEdits onChanged={load} />
          </div>
        </TabsContent>

        {rights.can_delete_customers && (
          <TabsContent value="history">
            <div className="p-4 sm:p-6">
              <CustomerChangeHistory />
            </div>
          </TabsContent>
        )}

        {rights.can_delete_customers && (
          <TabsContent value="archived">
            <div className="p-4 sm:p-6">
              <ArchivedCustomers onChanged={load} />
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

export function CustomerDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [statement, setStatement] = useState(null);

  useEffect(() => {
    api.get(`/customers/${id}`).then(({ data }) => setC(data)).catch(() => setC(false));
  }, [id]);

  if (c === null)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  if (c === false) return <Empty title="Customer not found" />;

  return (
    <div data-testid="customer-detail">
      <PageHeader
        title={c.company}
        subtitle={`${c.code} • ${c.city || ""} ${c.state || ""} • GSTIN ${c.gstin || "-"}`}
        actions={
          <>
            <Button variant="outline" className="gap-1.5" data-testid="customer-statement-button"
              onClick={() => setStatement({ id, name: c.company })}>
              <FileText className="h-4 w-4" /> Statement
            </Button>
            <Button variant="outline" asChild className="gap-1.5">
              <Link to="/customers">
                <ArrowLeft className="h-4 w-4" /> All customers
              </Link>
            </Button>
          </>
        }
      />
      <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-[320px_1fr]">
        <div className="lg:col-span-2">
          <DocumentJourney entity="customer" id={id} compact />
        </div>
        <div className="surface divide-y divide-border">
          {[
            ["Contact person", c.contact_person],
            ["Mobile", c.mobile],
            ["WhatsApp", c.whatsapp],
            ["Email", c.email],
            ["Billing address", c.billing_address],
            ["Site address", c.shipping_address],
            ["State / Place of supply", c.place_of_supply || c.state],
            ["Payment terms", c.payment_terms],
            ["Credit limit", inr(c.credit_limit)],
            ["Industry", c.industry],
            ["Lead source", c.lead_source],
          ].map(([k, v]) => (
            <div key={k} className="px-4 py-2.5">
              <div className="overline">{k}</div>
              <div className="text-sm">{v || "—"}</div>
            </div>
          ))}
        </div>

        <div className="space-y-4">
        {c.summary && (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="customer-360-summary">
            {[
              ["Quoted", inr(c.summary.quoted_value), `${c.summary.quotations} quotations`],
              ["Billed", inr(c.summary.billed), `${c.summary.invoices} invoices`],
              ["Received", inr(c.summary.received), "payments"],
              ["Outstanding", inr(c.summary.outstanding),
                c.summary.overdue_invoices ? `${c.summary.overdue_invoices} overdue` : "on time"],
            ].map(([k, v, sub]) => (
              <div key={k} className="surface p-3">
                <div className="overline">{k}</div>
                <div className="font-mono-num text-base font-semibold">{v}</div>
                <div className="text-[11px] text-muted-foreground">{sub}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="quotations">
          <TabsList data-testid="customer-timeline-tabs" className="flex-wrap">
            <TabsTrigger value="quotations">Quotations</TabsTrigger>
            <TabsTrigger value="invoices" data-testid="tab-customer-invoices">Invoices</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-customer-payments">Payments</TabsTrigger>
            <TabsTrigger value="challans" data-testid="tab-customer-challans">Deliveries</TabsTrigger>
            <TabsTrigger value="leads">Leads</TabsTrigger>
            <TabsTrigger value="enquiries">Enquiries</TabsTrigger>
            <TabsTrigger value="orders">Orders</TabsTrigger>
            <TabsTrigger value="followups">Follow-ups</TabsTrigger>
          </TabsList>
          <TabsContent value="quotations">
            <TimelineTable
              rows={c.quotations}
              cols={QUOTATION_COLS}
            />
          </TabsContent>
          <TabsContent value="invoices">
            <TimelineTable rows={c.invoices} cols={INVOICE_COLS} />
          </TabsContent>
          <TabsContent value="payments">
            <TimelineTable rows={c.payments} cols={PAYMENT_COLS} />
          </TabsContent>
          <TabsContent value="challans">
            <TimelineTable rows={c.challans} cols={CHALLAN_COLS} />
          </TabsContent>
          <TabsContent value="leads">
            <TimelineTable
              rows={c.leads}
              cols={LEAD_COLS}
            />
          </TabsContent>
          <TabsContent value="enquiries">
            <TimelineTable
              rows={c.enquiries}
              cols={ENQUIRY_COLS}
            />
          </TabsContent>
          <TabsContent value="orders">
            <TimelineTable
              rows={c.sales_orders}
              cols={ORDER_COLS}
            />
          </TabsContent>
          <TabsContent value="followups">
            <TimelineTable
              rows={c.follow_ups}
              cols={FOLLOWUP_COLS}
            />
          </TabsContent>
        </Tabs>
        </div>
      </div>
      <StatementDialog customer={statement} onClose={() => setStatement(null)} />
    </div>
  );
}

function TimelineTable({ rows = [], cols }) {
  if (!rows.length)
    return <div className="surface mt-3 p-8 text-center text-sm text-muted-foreground">No records yet</div>;
  return (
    <div className="surface mt-3 overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {cols.map(([k, label]) => (
              <TableHead key={k}>{label}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id}>
              {cols.map(([k, , render]) => (
                <TableCell key={k} className="text-sm">
                  {render ? render(r) : r[k] || "—"}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
